import React, { useState } from 'react';
import moment from 'moment';
import { FaChevronDown } from 'react-icons/fa6';
import './mobile.css';

const MOBILE_MARKET_TABS = ['Overview', 'Detail', 'Timeline'];

const getListingName = (row, kind) => {
  const listingFor =
    kind === 'offer' ? row?.offerCreatedFor : row?.demandCreatedFor;

  if (listingFor === 'NON_REGISTERED_USER') {
    return row?.nonRegisteredUserName || 'Not Available';
  }

  return row?.trader?.entityName || 'Not Available';
};

const getListingPhone = (row, kind) => {
  const listingFor =
    kind === 'offer' ? row?.offerCreatedFor : row?.demandCreatedFor;

  if (listingFor === 'NON_REGISTERED_USER') {
    if (!row?.nonRegisteredUserPhoneNumber) return 'No phone';
    return `${row?.nonRegisteredUserCallingCode || ''}${row?.nonRegisteredUserCallingCode ? '-' : ''}${row?.nonRegisteredUserPhoneNumber}`;
  }

  if (!row?.trader?.phone) return 'No phone';
  return `${row?.trader?.callingCode?.callingCode || ''}${row?.trader?.callingCode?.callingCode ? '-' : ''}${row?.trader?.phone}`;
};

const getCreatedByName = (row) => {
  const createdByUser = row?.createdByUser;
  if (!createdByUser) return 'N/A';

  return (
    createdByUser?.entityName ||
    createdByUser?.tradeName ||
    [createdByUser?.firstName, createdByUser?.lastName]
      .filter(Boolean)
      .join(' ') ||
    'N/A'
  );
};

const getChildUserName = (row) => {
  const createdByUser = row?.createdByUser;
  if (!createdByUser) return 'N/A';

  if (
    createdByUser?.phone &&
    row?.trader?.phone &&
    createdByUser?.phone === row?.trader?.phone
  ) {
    return '-';
  }

  return (
    [createdByUser?.firstName, createdByUser?.lastName]
      .filter(Boolean)
      .join(' ') || 'N/A'
  );
};

const getGradeLabel = (row) =>
  row?.grades
    ?.map((grade) => grade?.name)
    .filter(Boolean)
    .join(', ') || 'N/A';

const getLocationLabel = (row) => {
  if (row?.portsOfDischarge?.length) {
    return row.portsOfDischarge
      .map((port) => port?.name)
      .filter(Boolean)
      .join(', ');
  }

  if (row?.portOfDischarge?.name) {
    return row.portOfDischarge.name;
  }

  const city = row?.city?.cityName;
  const state = row?.state?.stateName;
  return [city, state].filter(Boolean).join(', ') || 'Not Available';
};

const getQuantityLabel = (value, row) => {
  if (value === null || value === undefined) return 'N/A';
  return `${value} ${row?.actualQuantityUnit || row?.quantityUnit || ''}`.trim();
};

const getRateLabel = (row) => {
  if (row?.rate === null || row?.rate === undefined) return 'N/A';
  const currency = row?.rateUnit?.isoCurrencyCode || '';
  return `${currency ? `${currency} ` : ''}${row?.rate}/${row?.quantityUnit || ''}`.trim();
};

const formatDate = (value, format = 'D MMM, YYYY hh:mm A') => {
  if (!value) return 'N/A';
  return moment(value).format(format);
};

const getTimelineRows = (row, kind) => {
  const rows = [];

  if (row?.validityStartTime) {
    rows.push({ label: 'Created', value: formatDate(row.validityStartTime) });
  }

  if (row?.validityExpireTime) {
    rows.push({ label: 'Expiry', value: formatDate(row.validityExpireTime) });
  }

  if (row?.minShipmentDelay || row?.maxShipmentDelay) {
    rows.push({
      label: 'Shipment',
      value: `${formatDate(row?.minShipmentDelay, 'D MMM, YYYY')} - ${formatDate(row?.maxShipmentDelay, 'D MMM, YYYY')}`,
    });
  }

  if (row?.minDeliveryDelay || row?.maxDeliveryDelay) {
    rows.push({
      label: 'Delivery',
      value: `${formatDate(row?.minDeliveryDelay, 'D MMM, YYYY')} - ${formatDate(row?.maxDeliveryDelay, 'D MMM, YYYY')}`,
    });
  }

  if (!rows.length) {
    rows.push({
      label: kind === 'offer' ? 'Listing date' : 'Demand date',
      value: formatDate(row?.createdAt),
    });
  }

  return rows;
};

const getStatusTone = (value) => {
  const normalizedValue = `${value || ''}`.toLowerCase();

  if (normalizedValue.includes('active') || normalizedValue.includes('live'))
    return 'success';
  if (
    normalizedValue.includes('pending') ||
    normalizedValue.includes('forward')
  )
    return 'warning';
  if (
    normalizedValue.includes('inactive') ||
    normalizedValue.includes('expired') ||
    normalizedValue.includes('foreign')
  ) {
    return 'danger';
  }

  return 'muted';
};

const getMobileSections = (
  row,
  index,
  filters,
  kind,
  renderStatusControl,
  renderRowAction,
) => {
  const sequenceNumber =
    ((filters?.pageNo || 1) - 1) * (filters?.pageSize || 10) + index + 1;
  const listingId = row?.id || 'N/A';
  const listingType = kind === 'offer' ? row?.offerType : row?.demandType;

  return {
    Overview: [
      { label: 'S.No', value: sequenceNumber },
      { label: 'Type', value: listingType || 'N/A', tone: 'badge' },
      { label: 'Status', value: row?.status || 'N/A', control: 'status' },
      ...(renderRowAction
        ? [{ label: 'Action', value: '', control: 'action' }]
        : []),
    ],
    Detail: [
      {
        label: kind === 'offer' ? 'Seller' : 'Buyer',
        value: getListingName(row, kind),
        control: 'name',
      },
      { label: kind === 'offer' ? 'Offer ID' : 'Demand ID', value: listingId },
      { label: 'Phone', value: getListingPhone(row, kind) },
      { label: 'Created By', value: getCreatedByName(row) },
      { label: 'Child User', value: getChildUserName(row) },
      { label: 'Min Qty', value: getQuantityLabel(row?.minimumQty, row) },
    ],
    Timeline: getTimelineRows(row, kind),
    renderStatusControl,
    renderRowAction,
  };
};

const MarketListingMobileView = ({
  rows,
  filters,
  kind,
  handlePageChange,
  totalPages,
  currentPageNo,
  currentPageSize,
  onIdClick,
  onNameClick,
  renderStatusControl,
  renderRowAction,
  children,
}) => {
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [activeMobileTabs, setActiveMobileTabs] = useState({});

  return (
    <>
      <div className="market-mobile-list">
        {(rows || []).map((row, index) => {
          const activeTab = activeMobileTabs[row.id] || MOBILE_MARKET_TABS[0];
          const sections = getMobileSections(
            row,
            index,
            filters,
            kind,
            renderStatusControl,
            renderRowAction,
          );
          const activeSection = sections[activeTab] || [];
          const isExpanded = expandedCardId === row.id;

          return (
            <div className="market-mobile-card" key={row.id}>
              <div className="market-mobile-card__summary">
                <div className="market-mobile-card__identity">
                  <div className="market-mobile-card__product-line">
                    <div className="market-mobile-card__grade">
                      <span className="market-mobile-card__fact-label">Grade</span>
                      <strong>{getGradeLabel(row)}</strong>
                    </div>
                    <div className="market-mobile-card__location">
                      <span className="market-mobile-card__fact-label">Location</span>
                      <span title={getLocationLabel(row)}>{getLocationLabel(row)}</span>
                    </div>
                  </div>
                  <div className="market-mobile-card__summary-facts">
                    <span>
                      <span className="market-mobile-card__fact-label">Total Qty</span>
                      <strong>{getQuantityLabel(row?.totalQty, row)}</strong>
                    </span>
                    <span>
                      <span className="market-mobile-card__fact-label">Rate</span>
                      <strong>{getRateLabel(row)}</strong>
                    </span>
                  </div>
                </div>
                <div className="market-mobile-card__aside">
                  <span
                    className={`market-mobile-card__pill ${getStatusTone(row?.status)}`}
                  >
                    {row?.status || 'N/A'}
                  </span>
                  <div className="market-mobile-card__tools">
                    <button
                      type="button"
                      className={`market-mobile-card__expand-toggle ${isExpanded ? 'is-open' : ''}`}
                      onClick={() =>
                        setExpandedCardId((prev) =>
                          prev === row.id ? null : row.id,
                        )
                      }
                      aria-expanded={isExpanded}
                      aria-label={
                        isExpanded
                          ? 'Collapse listing details'
                          : 'Expand listing details'
                      }
                    >
                      <FaChevronDown />
                    </button>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="market-mobile-card__body">
                  <div className="market-mobile-card__tabs">
                    {MOBILE_MARKET_TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        className={`market-mobile-card__tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() =>
                          setActiveMobileTabs((prev) => ({
                            ...prev,
                            [row.id]: tab,
                          }))
                        }
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="market-mobile-card__detail-list">
                    {activeSection.map((item) => {
                      let content = item.value;

                      if (item.control === 'status') {
                        content = (
                          <div className="market-mobile-card__control-wrap">
                            {renderStatusControl(row, index)}
                          </div>
                        );
                      } else if (item.control === 'action') {
                        content = (
                          <div className="market-mobile-card__control-wrap">
                            {renderRowAction(row, index)}
                          </div>
                        );
                      } else if (item.control === 'name' && onNameClick) {
                        content = (
                          <button
                            type="button"
                            className="market-mobile-card__detail-link"
                            onClick={() => onNameClick(row)}
                          >
                            {item.value}
                          </button>
                        );
                      } else if (
                        item.label ===
                        (kind === 'offer' ? 'Offer ID' : 'Demand ID')
                      ) {
                        content = (
                          <button
                            type="button"
                            className="market-mobile-card__detail-link"
                            onClick={() => onIdClick?.(row)}
                          >
                            {item.value}
                          </button>
                        );
                      } else if (item.tone === 'badge') {
                        content = (
                          <span
                            className={`market-mobile-card__pill ${getStatusTone(item.value)}`}
                          >
                            {item.value}
                          </span>
                        );
                      }

                      return (
                        <div
                          className="market-mobile-card__detail-row"
                          key={item.label}
                        >
                          <div className="market-mobile-card__detail-label">
                            {item.label}
                          </div>
                          <div className="market-mobile-card__detail-value">
                            {content}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages ? (
        <div className="market-mobile-pagination">
          <span className="market-mobile-pagination__count">
            {currentPageNo} of {totalPages}
          </span>
          <select
            className="page-select form-select table-select f-12 fw-normal market-mobile-pagination__select"
            value={currentPageSize}
            onChange={(e) => {
              const nextPageSize = Number(e.target.value);
              handlePageChange({
                pageNo: totalPages === currentPageNo ? 1 : currentPageNo,
                pageSize: nextPageSize,
              });
            }}
          >
            {[10, 20, 30, 40, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
          <div className="pagination market-mobile-pagination__actions">
            <button
              type="button"
              onClick={() =>
                handlePageChange({ pageNo: 1, pageSize: currentPageSize })
              }
              disabled={currentPageNo <= 1}
            >
              {'<<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: currentPageNo - 1,
                  pageSize: currentPageSize,
                })
              }
              disabled={currentPageNo === 1}
            >
              {'<'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: currentPageNo + 1,
                  pageSize: currentPageSize,
                })
              }
              disabled={currentPageNo >= totalPages}
            >
              {'>'}
            </button>
            <button
              type="button"
              onClick={() =>
                handlePageChange({
                  pageNo: totalPages,
                  pageSize: currentPageSize,
                })
              }
              disabled={currentPageNo >= totalPages}
            >
              {'>>'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="market-mobile-hide-table">{children}</div>
    </>
  );
};

export default MarketListingMobileView;
